import React, { Component } from "react";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import Dropzone from "react-dropzone";
import classNames from "classnames";
import Button from "components/Common/Button";
import { adaUploader } from "services/uploader";
import ProgressBar from "components/Common/ProgressBar";
import { createAlert as createAlertAction } from "actions/alerts";
import DebouncedInput from "components/Common/DebouncedInput";
import "./style.scss";

/**
 * An input field that is used to upload media object.
 * Files can be linked, uploaded or dragged and dropped into the text field.
 * Also generates upload, clear and view in a new window buttons dependant on if
 * a user has uploaded a file.
 * Can upload: gifs, png, svg, jpeg, jpg
 */
export class MediaUploader extends Component {
  /**
   * Opens a new window to view the uploaded media.
   */
  static previewMedia() {
    const { urlPath, urlField } = this.props;
    window.open(urlPath.get(urlField), "_blank");
  }

  /**
   * creates the element
   * @param {Object} props
   */
  constructor(props) {
    super(props);
    this.dropZoneRef = React.createRef();
    this.inputOnChange = this.inputOnChange.bind(this);
    this.handleFileUpload = this.handleFileUpload.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.clearButtonOnClick = this.clearButtonOnClick.bind(this);
    this.openDropZoneRef = this.openDropZoneRef.bind(this);
    this.onUploadProgress = this.onUploadProgress.bind(this);
    MediaUploader.previewMedia = MediaUploader.previewMedia.bind(this);
    this.onSuccess = this.onSuccess.bind(this);
    this.onFailure = this.onFailure.bind(this);

    this.state = {
      progress: 0,
      decayRemainder: 8,
      fadeLoader: false,
      isLoading: false
    };
    /** @constant */
    this.PROGRESSCONSTANT = 90;
    /** @constant */
    this.DECAYINTERVAL = 250;
    /** @constant */
    this.MEGABYTEMULTIPLIER = 1000000;
    /** @constant */
    this.MAXMEGABYTE = 20;
    /** @constant */
    this.DECAYREMAINDER = 8;
    /** @constant */
    this.REMAINDERDIVISOR = 1.25;
    /** @constant */
    this.PROGRESSUPPER = 98;
    /** @constant */
    this.TIMEOUT = 500;
  }

  /**
   * Updates the progress bar with the file upload progress
   * @param {Object} progressEvent contains information regarding the files upload progress
   */
  onUploadProgress(progressEvent) {
    const { loaded, total } = progressEvent;
    const progress = Math.round((loaded / total) * this.PROGRESSCONSTANT);

    // Go to 90 to spoof delay between 100% completetion and "done" callback
    this.setState(
      {
        progress
      },
      () => {
        if (loaded === total) {
          this.decayInterval = setInterval(() => this.decayTime(), this.DECAYINTERVAL);
        }
      }
    );
  }

  /**
   * handles the uploading of files dropped in the drop zone
   *  @param {file} files files that are being uploaded
   */
  onDrop(files) {
    this.setState({ isLoading: true });
    this.handleFileUpload(files[0]);
  }

  /**
   * Function for succssful media uplaod
   * @param {String} url
   * @param {Number} dimensions
   */
  onSuccess(url, dimensions) {
    const { onChange } = this.props;
    this.setState({
      progress: 100,
      decayRemainder: this.DECAYREMAINDER,
      fadeLoader: true
    });
    clearInterval(this.decayInterval);

    setTimeout(() => {
      onChange(url, "", dimensions);
      this.setState({ progress: 0, fadeLoader: false, isLoading: false });
    }, this.TIMEOUT);
  }

  /**
   * Function for failed media upload
   * @param {Object} error
   */
  onFailure(error) {
    const { createAlert, failureText } = this.props;

    this.setState({
      progress: 0,
      decayRemainder: this.DECAYREMAINDER,
      fadeLoader: false,
      isLoading: false
    });
    createAlert({ message: failureText || error.data.message });
    clearInterval(this.decayInterval);
  }

  /**
   * Action that handles uploading an image from the media uplaoder component.
   * @param {String} endpoint where to send the upload request to
   * @param {Object} file the file to upload
   * @returns {String} path to the files upload location
   */
  uploadMedia(endpoint, file) {
    const formData = new FormData();
    formData.append("file", file);

    return adaUploader(this.onUploadProgress)
      .post(endpoint, formData)
      .then((response) => {
        this.onSuccess(response.data.url, response.data.dimensions);
      })
      .catch((error) => {
        this.onFailure(error.response);
      });
  }

  /**
   * handles the on change function for the input fields, takes in the url fields
   * and file dimsnesions
   * it then sends the props, url fields and the file dimensions to the onChange
   * function passed to the uploader.
   * @param {String} urlFieldVal url field
   * @param {String} richUrlFieldVal the rich url field nothing if not passed
   * @param {String} dimensions file dimensions, nothing if not passed
   */
  inputOnChange(urlFieldVal, richUrlFieldVal = "", dimensions = "") {
    const { onChange } = this.props;
    onChange(urlFieldVal, richUrlFieldVal, dimensions);
  }

  /**
   * Method that calls the action that uploads the file. Alerts the user if the file is to big.
   * The function also uses the passed onChange function to  handle front and back end changes.
   * @param {Object} file
   * @returns {String | Null}
   */
  handleFileUpload(file) {
    const { endpoint, createAlert } = this.props;

    // Catch images that are larger than 20MB
    if (file.size / this.MEGABYTEMULTIPLIER > this.MAXMEGABYTE) {
      let alertMessage;

      if (file.type.includes("image")) {
        alertMessage = `Images must be smaller than ${this.MAXMEGABYTE}MB.`;
      } else if (file.type.includes("video")) {
        alertMessage = `Videos must be smaller than ${this.MAXMEGABYTE}MB.`;
      }

      createAlert({ message: alertMessage });
      this.setState({ isLoading: false });

      return alertMessage;
    }

    if (file) {
      this.uploadMedia(
        endpoint,
        file
      );
    }

    return null;
  }

  /**
   * A timer that sets the delay between upload progress updates.
   */
  decayTime() {
    const { decayRemainder } = this.state;
    this.setState({
      progress: this.PROGRESSUPPER - decayRemainder,
      decayRemainder: decayRemainder / this.REMAINDERDIVISOR
    });
  }

  /**
   * clears the media uploader and hides the clearn and pop out buttons
   */
  clearButtonOnClick() {
    const { onChange } = this.props;
    onChange("", "");
  }

  /**
   * opens the file explorer program for teh local OS
   */
  openDropZoneRef() {
    this.dropZoneRef.current.open();
  }

  /**
   * Renders the clear, view media and upload media buttons.
   * @returns {ReactElement}
   */
  renderButtons() {
    const { urlPath, urlField, uploadIcon } = this.props;

    if (urlPath.get(urlField)) {
      return (
        <div className="MediaUploader__buttons">
          <Button
            icon="PopOut"
            fillColor="currentColor"
            clear
            onClick={MediaUploader.previewMedia}
            customClassName="MediaUploader__buttons__button"
          />
          <Button
            icon="Clear"
            fillColor="currentColor"
            clear
            onClick={this.clearButtonOnClick}
            customClassName="MediaUploader__buttons__button"
          />
        </div>
      );
    }

    return (
      <Button
        icon={uploadIcon}
        fillColor="currentColor"
        onClick={this.openDropZoneRef}
        customClassName="MediaUploader__upload-button"
        clear
      />
    );
  }

  /**
   * renders the element
   * @returns {ReactElement}
   */
  render() {
    const {
      urlPath,
      urlField,
      customClassName,
      name,
      invalidFields,
      placeholder,
      tabIndex,
      id,
      acceptTypes,
      notValid
    } = this.props;
    const { isLoading, fadeLoader, progress } = this.state;

    return (
      <div
        className={classNames("MediaUploader", {
          [customClassName]: customClassName,
          "MediaUploader--loading": isLoading
        })}
      >
        {isLoading && (
          <ProgressBar
            customClassName={classNames("MediaUploader__progress-bar", {
              "MediaUploader__progress-bar--hide": fadeLoader
            })}
            percentDone={progress}
          />
        )}
        <div className="MediaUploader__input-wrap">
          {/** allows users to drag and drop files into the text bar */}
          <Dropzone
            ref={this.dropZoneRef}
            onDropAccepted={this.onDrop}
            disableClick
            accept={acceptTypes}
            activeClassName="MediaUploader__dropzone--active"
            className="MediaUploader__dropzone"
          >
            <DebouncedInput
              id={id}
              name={name}
              customClassName={classNames("g-input--media-url", "MediaUploader__dropzone__input", {
                "g-input--invalid": invalidFields && invalidFields.includes(urlField)
              })}
              placeholder={placeholder}
              value={urlPath.get(urlField)}
              onChange={this.inputOnChange}
              tabIndex={tabIndex}
              isInvalid={notValid}
            />
          </Dropzone>
          <div className="ResponseEditorMediaUploader__buttons-wrap">{this.renderButtons()}</div>
        </div>
      </div>
    );
  }
}

MediaUploader.propTypes = {
  id: PropTypes.string,
  customClassName: PropTypes.string,
  tabIndex: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  urlPath: ImmutablePropTypes.map.isRequired,
  urlField: PropTypes.string.isRequired,
  acceptTypes: PropTypes.string,
  placeholder: PropTypes.string,
  failureText: PropTypes.string,
  endpoint: PropTypes.string.isRequired,
  uploadIcon: PropTypes.string.isRequired,
  invalidFields: ImmutablePropTypes.list,
  createAlert: PropTypes.func.isRequired,
  notValid: PropTypes.bool
};

/**
 * Sets default properties
 */
MediaUploader.defaultProps = {
  id: "mediaUploader",
  customClassName: null,
  tabIndex: null,
  name: "mediaUploader",
  placeholder: "media url",
  failureText: "Something went wrong",
  invalidFields: null,
  notValid: false,
  acceptTypes: null
};

/**
 * maps action
 * @param {Function} dispatch
 * @returns {Object}
 */
function mapDispatch(dispatch) {
  return {
    createAlert: bindActionCreators(createAlertAction, dispatch)
  };
}

const Connector = connect(
  null,
  mapDispatch
)(MediaUploader);

export default Connector;
